<template>
  <core-popup
    id="gp_autotest_remove_resource_popup"
    show
    :title="title"
    :class="popup.remove_popup"
    :close-icon="true"
    @onClickCancel="onClose"
  >
    <template #body>
      <div>
        <div
          :class="[popup.message, hasAssignedToTasks && canConvertToVirtualResource ? popup.message_convert : '']"
          v-html="message"
        />
      </div>
      <div :class="popup.buttons">
        <vgp-button
          v-if="hasAssignedToTasks && canConvertToVirtualResource"
          :style="{marginLeft: 16 + 'px'}"
          type="secondary"
          :label="locales('convert_to_virtual_resource_button')"
          :small="true"
          :is-loading="isConverting"
          @onClick="onConvert"
        />
        <vgp-button
          v-else
          id="gp_autotest_remove_resource_popup_btn_cancel"
          type="secondary"
          :label="locales('common_cancel')"
          :small="true"
          :disabled="isConverting"
          @onClick="onClose"
        />

        <vgp-button
          id="gp_autotest_remove_resource_popup_btn_delete"
          :style="{marginLeft: 16 + 'px'}"
          type="destructive"
          :label="locales('common_delete')"
          :small="true"
          :disabled="isConverting"
          @onClick="onDelete"
        />
      </div>
    </template>
  </core-popup>
</template>

<script>
import CorePopup from '$$vueCmp/popups/corePopup/corePopup.vue';
import VgpButton from '$$vueCmp/globalButton/vgpButton.vue';
import pricingHelper from '../../../../helpers/pricingHelper';

export default {
  components: {
    CorePopup,
    VgpButton,
  },
  props: {
    config: { type: Object, required: true, default: { type: 'account', resources: [], projectId: null } },
  },
  emits: {
    onClose: Boolean,
    removeResourcesFromAccount: Array,
    removeResourcesFromProject: Array,
    convertAccountResourcesToVirtual: Array,
  },
  data() {
    return {
      locales: __,
      keys: {
        account: {
          people: {
            title: __('remove_popup_people'),
            message: __('resources_member_delete_confirm_text'),
            assignedMessage: __('resources_delete_confirm_change_assign_text'),
            deleteMessageWithConvert: __('resources_delete_confirm_convert_text'),
          },
          material: {
            title: __('remove_popup_material'),
            message: __('resources_delete_confirm_text'),
            assignedMessage: __('resources_material_delete_confirm_change_assign_text'),
            multiDeleteMessage: __('resources_delete_confirm_text_multi'),
            multiDeleteMessageWithAssigned: __('resources_delete_confirm_change_assign_text_multi'),
          },
        },
        project: {
          people: {
            title: __('remove_popup_people_from_project'),
            message: __('resources_member_delete_confirm_text'),
            assignedMessage: __('resources_delete_confirm_change_assign_text'),
          },
          material: {
            title: __('remove_popup_material_from_project'),
            message: __('resources_delete_confirm_text'),
            assignedMessage: __('resources_material_delete_confirm_change_assign_text'),
            multiDeleteMessage: __('resources_delete_confirm_text_multi'),
            multiDeleteMessageWithAssigned: __('resources_delete_confirm_change_assign_text_multi'),
          },
        },
      },
      isConverting: false,
    };
  },
  computed: {
    resourcesType() {
      const people = this.config.resources.filter(res => res.userId);

      return people.length ? 'people' : 'material';
    },
    isMulti() {
      return this.config.resources.length > 1;
    },
    hasAssignedToTasks() {
      let isAssigned = false;

      if (this.config.type === 'account') {
        this.config.resources.forEach(resource => {
          const resourceData = this.$store.getters['resourcesModel/getResourceById'](resource.id);

          resourceData?.resourceProjects.forEach(pr => {
            const tasks = this.$store.getters['tasksModel/getItem'](pr.projectId)?.tasks;

            tasks?.forEach(task => {
              const assignedTask = task.resources.find(res => res.resource_id === resource.id);

              if (assignedTask) {
                isAssigned = true;
              }
            });
          });
        });
      }

      if (this.config.type === 'project') {
        const tasks = this.$store.getters['tasksModel/getItem'](this.config.projectId)?.tasks;

        tasks?.forEach(task => {
          const assignedTask = task.resources.some(res => this.config.resources.find(resource => resource.id === res.resource_id));

          if (assignedTask) {
            isAssigned = true;
          }
        });
      }

      return isAssigned;
    },
    canConvertToVirtualResource() {
      return this.config.type === 'account' && this.resourcesType === 'people' && pricingHelper.checkPricingAccess('convert_to_virtual');
    },
    title() {
      return this.keys[this.config.type][this.resourcesType].title;
    },
    message() {
      let message = this.keys[this.config.type][this.resourcesType].message;

      if (this.canConvertToVirtualResource && this.hasAssignedToTasks) {
        message = this.keys[this.config.type][this.resourcesType].deleteMessageWithConvert;
      } else if (this.hasAssignedToTasks) {
        message = this.keys[this.config.type][this.resourcesType].assignedMessage;
      }

      if (this.isMulti) {
        message = this.keys[this.config.type][this.resourcesType].multiDeleteMessage;

        if (this.hasAssignedToTasks) {
          message = this.keys[this.config.type][this.resourcesType].multiDeleteMessageWithAssigned;
        }
      }

      return message;
    },
  },
  methods: {
    onConvert() {
      if (this.config.type === 'account') {
        const resourceIds = this.config.resources.map(res => res.id);

        this.isConverting = true;
        this.$emit('convertAccountResourcesToVirtual', resourceIds, () => {
          this.isConverting = false;
        });
      }
    },

    onDelete() {
      if (this.config.type === 'account') {
        const resourcesIds = this.config.resources.map(res => res.id);
        let type = null;

        if (this.config.resources[0]) {
          type = this.config.resources[0].userId ? 'people' : 'material';
        }

        this.$emit('removeResourcesFromAccount', resourcesIds, type);
      }

      if (this.config.type === 'project') {
        const resources = this.config.resources.map(res => ({
          resourceId: res.id,
          projectId: this.config.projectId,
        }));

        this.$emit('removeResourcesFromProject', resources);
      }
    },

    onClose() {
      if (!this.isConverting) {
        this.$emit('onClose');
      }
    },
  },
};
</script>

<style module='popup' src='./popups.less'></style>
